
import React from "react";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";

import { countUpData } from "../constants";

/* ============================================
   COUNTUP STATISTICS SECTION
============================================ */

function CountupCard() {
  const { i18n } = useTranslation();

  // Language only.
  // RTL / LTR is managed globally.
  const language = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en")
    ? "en"
    : "ar";

  const isEnglish = language === "en";

  const content = {
    eyebrow: isEnglish
      ? "OUR PROFESSIONAL JOURNEY"
      : "مسيرتنا المهنية",

    title: isEnglish
      ? "Our Office in Numbers"
      : "مؤسسة النعمان بالأرقام",

    description: isEnglish
      ? "A look at the figures that reflect our professional journey."
      : "نظرة على الأرقام التي تعكس مسيرتنا المهنية.",
  };

  return (
    <section
      aria-labelledby="statistics-heading"
      className="
        relative isolate
        w-full overflow-hidden

        border-b border-[#D7E2EA]
        bg-[#EAF0F5]

        py-12
        sm:py-14
        lg:py-16

        dark:border-white/10
        dark:bg-[#13293B]

        transition-colors duration-300
      "
    >
      {/* ======================================
          BACKGROUND DETAILS
      ====================================== */}

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute -end-32 -top-40
          h-[400px] w-[400px]
          rounded-full
          bg-white/40
          blur-3xl

          dark:bg-[#294961]/20
        "
      />

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute -start-32 -bottom-48
          h-[360px] w-[360px]
          rounded-full
          bg-[#C19B60]/[0.035]
          blur-3xl

          dark:bg-[#D7B77F]/[0.025]
        "
      />

      <div className="container relative z-10">

        {/* ======================================
            SECTION HEADING
        ====================================== */}

        <div
          className="
            mb-9 flex flex-col
            items-center text-center
            sm:mb-11
          "
        >
          {/* Eyebrow */}
          <div className="mb-3 flex items-center gap-3">

            <span
              aria-hidden="true"
              className="
                h-px w-7
                bg-skin-accent
              "
            />

            <span className="eyebrow text-skin-accent">
              {content.eyebrow}
            </span>

            <span
              aria-hidden="true"
              className="
                h-px w-7
                bg-skin-accent
              "
            />

          </div>

          {/* Heading */}
          <h2
            id="statistics-heading"
            className="
              h4 text-skin-heading
            "
          >
            {content.title}
          </h2>

          {/* Description */}
          <p
            className="
              body-3
              mt-3 max-w-[550px]
              text-skin-muted
            "
          >
            {content.description}
          </p>

        </div>

        {/* ======================================
            MAIN STATISTICS CARD
        ====================================== */}

        <div
          className="
            relative overflow-hidden

            rounded-[24px]
            border border-[#D9E3EB]

            bg-white

            shadow-[0_12px_40px_rgba(19,41,59,0.05)]

            dark:border-white/10
            dark:bg-[#1C354A]
            dark:shadow-[0_20px_50px_rgba(0,0,0,0.12)]

            transition-colors duration-300
          "
        >
          {/* Subtle top accent */}
          <div
            aria-hidden="true"
            className="
              absolute inset-x-0 top-0
              z-10 h-px

              bg-gradient-to-r
              from-transparent
              via-[#C19B60]/50
              to-transparent

              dark:via-[#D7B77F]/50
            "
          />

          {/* ==================================
              RESPONSIVE GRID
          ================================== */}

          <div className="grid grid-cols-2 lg:grid-cols-4">

            {countUpData.map((item, index) => {
              const label = isEnglish
                ? item.titleEn
                : item.titleAr;

              const count = Number(
                String(item.count).replace(/,/g, "")
              ) || 0;

              return (
                <article
                  key={item.id ?? index}
                  className="
                    group relative

                    flex min-w-0 flex-col
                    items-center justify-center

                    px-3 py-8
                    text-center

                    sm:px-6 sm:py-10
                    lg:px-5 lg:py-11
                    xl:px-8

                    transition-colors duration-300

                    hover:bg-[#F6F9FB]

                    dark:hover:bg-white/[0.035]
                  "
                >
                  {/* ==========================
                      ICON
                  ========================== */}

                  <div
                    className="
                      mb-6
                      flex h-[58px] w-[58px]
                      items-center justify-center

                      rounded-2xl
                      border border-[#DDE6EC]
                      bg-[#F1F5F8]

                      dark:border-white/10
                      dark:bg-[#294961]

                      transition-all duration-300

                      group-hover:-translate-y-1
                      group-hover:border-[#C19B60]/40

                      dark:group-hover:border-[#D7B77F]/40
                    "
                  >
                    <img
                      src={item.icon}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      decoding="async"
                      className="
                        h-9 w-9
                        object-contain
                      "
                    />
                  </div>

                  {/* ==========================
                      ANIMATED NUMBER
                  ========================== */}

                  <div
                    className="
                      font-cairo
                      text-[clamp(1.6rem,3.3vw,2.75rem)]
                      font-bold
                      leading-[1.35]
                      tracking-tight
                      tabular-nums

                      text-[#19344D]
                      dark:text-[#F5F8FB]
                    "
                    aria-hidden="true"
                  >
                    <CountUp
                      start={0}
                      end={count}
                      duration={2.6}
                      delay={0}
                      useEasing={true}
                      separator=","
                      enableScrollSpy={true}
                      scrollSpyOnce={true}
                    />
                  </div>

                  {/* Accessible static value */}
                  <span className="sr-only">
                    {count.toLocaleString("en-US")}
                    {" "}
                    {label}
                  </span>

                  {/* ==========================
                      GOLD ACCENT
                  ========================== */}

                  <span
                    aria-hidden="true"
                    className="
                      my-5
                      h-[2px] w-8
                      rounded-full

                      bg-[#C19B60]/70
                      dark:bg-[#D7B77F]/70

                      transition-all duration-300
                      group-hover:w-12
                    "
                  />

                  {/* ==========================
                      STATISTIC LABEL
                  ========================== */}

                  <h3
                    className="
                      body-2 font-medium

                      text-[#62778A]
                      dark:text-[#B2C4D3]

                      transition-colors duration-300

                      group-hover:text-[#19344D]
                      dark:group-hover:text-white
                    "
                  >
                    {label}
                  </h3>

                  {/* ==========================
                      MOBILE VERTICAL LINE
                  ========================== */}

                  {index % 2 === 0 && (
                    <span
                      aria-hidden="true"
                      className="
                        absolute end-0 top-1/2
                        h-[55%] w-px

                        -translate-y-1/2

                        bg-[#DDE6EC]
                        dark:bg-white/10

                        lg:hidden
                      "
                    />
                  )}

                  {/* ==========================
                      MOBILE HORIZONTAL LINE
                  ========================== */}

                  {index < countUpData.length - 2 && (
                    <span
                      aria-hidden="true"
                      className="
                        absolute inset-x-5 bottom-0
                        h-px

                        bg-[#DDE6EC]
                        dark:bg-white/10

                        lg:hidden
                      "
                    />
                  )}

                  {/* ==========================
                      DESKTOP VERTICAL LINE
                  ========================== */}

                  {index < countUpData.length - 1 && (
                    <span
                      aria-hidden="true"
                      className="
                        absolute end-0 top-1/2

                        hidden h-[52%] w-px
                        -translate-y-1/2

                        bg-[#DDE6EC]
                        dark:bg-white/10

                        lg:block
                      "
                    />
                  )}

                </article>
              );
            })}

          </div>
        </div>

        {/* ======================================
            BOTTOM DECORATIVE DETAIL
        ====================================== */}

        <div
          aria-hidden="true"
          className="
            mt-7 flex items-center
            justify-center gap-3
          "
        >
          <span className="h-px w-9 bg-skin-border" />

          <span
            className="
              h-1.5 w-1.5
              rounded-full
              bg-skin-accent
            "
          />

          <span className="h-px w-9 bg-skin-border" />
        </div>

      </div>
    </section>
  );
}

export default CountupCard;
